import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Edit2, Trash2, Plus, GripVertical } from 'lucide-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProjectChecklist, ChecklistItem } from '../../../types/projects';
import { ConfirmDeleteModal } from '../../../Components/ConfirmDeleteModal';

interface ProjectChecklistModalProps {
    show: boolean;
    onHide: () => void;
    onSaveTitle: (title: string) => Promise<void>;
    onAddItem: (text: string) => Promise<void>;
    onUpdateItem: (itemKey: string, text: string) => Promise<void>;
    onDeleteItem: (itemKey: string) => Promise<void>;
    onReorderItems?: (orderedItemKeys: string[]) => Promise<void>;
    existingChecklist?: ProjectChecklist | null;
    submitting?: boolean;
    isNewChecklist?: boolean;
}

// Sortable item component for the modal
interface SortableModalItemProps {
    item: ChecklistItem;
    isEditing: boolean;
    editingText: string;
    onEditingTextChange: (text: string) => void;
    onStartEdit: (item: ChecklistItem) => void;
    onSaveEdit: () => void;
    onCancelEdit: () => void;
    onDelete: (itemKey: string) => void;
    submitting: boolean;
}

const SortableModalItem: React.FC<SortableModalItemProps> = ({
    item,
    isEditing,
    editingText,
    onEditingTextChange,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onDelete,
    submitting,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.key });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="std-box"
        >
            <div
                style={{
                    border: isEditing ? '2px solid #007bff' : '1px solid #dee2e6',
                    padding: '10px 15px',
                    borderRadius: '10px',
                }}
            >
                {isEditing ? (
                    <div>
                        <Form.Control
                            type="text"
                            value={editingText}
                            onChange={(e) => onEditingTextChange(e.target.value)}
                            disabled={submitting}
                            autoFocus
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    onSaveEdit();
                                }
                            }}
                            className="mb-2"
                        />
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={onSaveEdit}
                                disabled={submitting || !editingText.trim()}
                            >
                                Spara
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={onCancelEdit}
                                disabled={submitting}
                            >
                                Avbryt
                            </Button>
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={() => onDelete(item.key)}
                                disabled={submitting}
                            >
                                <Trash2 size={14} className="me-1" />
                                Radera
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div
                            {...attributes}
                            {...listeners}
                            style={{
                                cursor: 'grab',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px',
                                color: '#999',
                            }}
                        >
                            <GripVertical size={16} />
                        </div>
                        <span style={{ flex: 1 }}>{item.text}</span>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => onStartEdit(item)}
                        >
                            <Edit2 size={14} />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export const ProjectChecklistModal: React.FC<ProjectChecklistModalProps> = ({
    show,
    onHide,
    onSaveTitle,
    onAddItem,
    onUpdateItem,
    onDeleteItem,
    onReorderItems,
    existingChecklist,
    submitting = false,
    isNewChecklist = false,
}) => {
    const [title, setTitle] = useState('');
    const [newItemText, setNewItemText] = useState('');
    const [editingItemKey, setEditingItemKey] = useState<string | null>(null);
    const [editingItemText, setEditingItemText] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteItemKey, setPendingDeleteItemKey] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Sort items by sortOrder
    const sortedItems = existingChecklist?.items
        ? Object.values(existingChecklist.items).sort((a, b) => {
            const orderA = a.sortOrder ?? 0;
            const orderB = b.sortOrder ?? 0;
            return orderA - orderB;
        })
        : [];

    // Handle drag end for reordering
    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id || !onReorderItems) return;

        const oldIndex = sortedItems.findIndex(item => item.key === active.id);
        const newIndex = sortedItems.findIndex(item => item.key === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const reorderedItems = arrayMove(sortedItems, oldIndex, newIndex);
            const orderedKeys = reorderedItems.map(item => item.key);
            await onReorderItems(orderedKeys);
        }
    };

    // Reset form when modal opens/closes
    useEffect(() => {
        if (show) {
            if (existingChecklist) {
                setTitle(existingChecklist.title);
            } else {
                setTitle('Att göra');
            }
            setNewItemText('');
            setEditingItemKey(null);
            setEditingItemText('');
        }
    }, [show, existingChecklist]);

    const handleSaveTitle = async () => {
        if (!title.trim()) return;

        try {
            await onSaveTitle(title.trim());
            if (isNewChecklist) {
                onHide();
            }
        } catch (err) {
            console.error('Error saving checklist title:', err);
        }
    };

    const handleAddItem = async () => {
        if (!newItemText.trim()) return;

        try {
            await onAddItem(newItemText.trim());
            setNewItemText('');
        } catch (err) {
            console.error('Error adding item:', err);
        }
    };

    const handleStartEditItem = (item: ChecklistItem) => {
        setEditingItemKey(item.key);
        setEditingItemText(item.text);
    };

    const handleSaveEditItem = async () => {
        if (!editingItemKey || !editingItemText.trim()) return;

        try {
            await onUpdateItem(editingItemKey, editingItemText.trim());
            setEditingItemKey(null);
            setEditingItemText('');
        } catch (err) {
            console.error('Error updating item:', err);
        }
    };

    const handleCancelEditItem = () => {
        setEditingItemKey(null);
        setEditingItemText('');
    };

    const handleDeleteItemClick = (itemKey: string) => {
        setPendingDeleteItemKey(itemKey);
        setShowDeleteModal(true);
    };

    const handleConfirmDeleteItem = async () => {
        if (!pendingDeleteItemKey) return;

        try {
            await onDeleteItem(pendingDeleteItemKey);
            if (editingItemKey === pendingDeleteItemKey) {
                setEditingItemKey(null);
                setEditingItemText('');
            }
        } catch (err) {
            console.error('Error deleting item:', err);
        } finally {
            setPendingDeleteItemKey(null);
        }
    };

    const handleClose = () => {
        setTitle('');
        setNewItemText('');
        setEditingItemKey(null);
        setEditingItemText('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {isNewChecklist ? 'Ny checklista' : 'Redigera checklista'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-4">
                        <Form.Label>Titel</Form.Label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Form.Control
                                type="text"
                                placeholder="Checklistatitel"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={submitting}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSaveTitle();
                                    }
                                }}
                                autoFocus={isNewChecklist}
                            />
                            {!isNewChecklist && (
                                <Button
                                    variant="primary"
                                    onClick={handleSaveTitle}
                                    disabled={submitting || !title.trim() || title.trim() === existingChecklist?.title}
                                >
                                    Spara
                                </Button>
                            )}
                        </div>
                    </Form.Group>

                    {/* Checklist Items - only show when editing existing checklist */}
                    {!isNewChecklist && existingChecklist && (
                        <div className="mb-3">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <Form.Label style={{ margin: 0, fontWeight: '500' }}>Checklistpunkter</Form.Label>
                            </div>

                            {/* Add new item form */}
                            <div className="std-box mb-3" style={{ backgroundColor: '#f8f9fa', padding: '16px' }}>
                                <Form.Group>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Ny checklistpunkt"
                                            value={newItemText}
                                            onChange={(e) => setNewItemText(e.target.value)}
                                            disabled={submitting}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddItem();
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="primary"
                                            onClick={handleAddItem}
                                            disabled={submitting || !newItemText.trim()}
                                        >
                                            <Plus size={16} className="me-1" />
                                            Lägg till
                                        </Button>
                                    </div>
                                </Form.Group>
                            </div>

                            {/* Items list */}
                            {sortedItems.length > 0 ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={sortedItems.map(item => item.key)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {sortedItems.map((item) => (
                                                <SortableModalItem
                                                    key={item.key}
                                                    item={item}
                                                    isEditing={editingItemKey === item.key}
                                                    editingText={editingItemText}
                                                    onEditingTextChange={setEditingItemText}
                                                    onStartEdit={handleStartEditItem}
                                                    onSaveEdit={handleSaveEditItem}
                                                    onCancelEdit={handleCancelEditItem}
                                                    onDelete={handleDeleteItemClick}
                                                    submitting={submitting}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            ) : (
                                <p style={{ color: '#666', fontStyle: 'italic', margin: 0 }}>Inga punkter ännu</p>
                            )}
                        </div>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                    {isNewChecklist ? 'Avbryt' : 'Stäng'}
                </Button>
                {isNewChecklist && (
                    <Button
                        variant="primary"
                        onClick={handleSaveTitle}
                        disabled={submitting || !title.trim()}
                    >
                        Skapa checklista
                    </Button>
                )}
            </Modal.Footer>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setPendingDeleteItemKey(null);
                }}
                onConfirm={handleConfirmDeleteItem}
                title="Radera punkt"
                message="Är du säker på att du vill radera denna punkt? Detta kan inte ångras."
            />
        </Modal>
    );
};
